<template>
  <div>
    <ys-chip-group
      orientation="vertical"
      :aria-label="chipGroupLabel"
      class="filter-options"
    >
      <filter-option
        v-for="option in displayValues"
        :key="option.id"
        :filter="option"
        :data-test="option.id"
        :disabled="disabled"
        @filter-toggled="toggleCheckboxHandler(option)"
      />
    </ys-chip-group>
  </div>
</template>
<!-- eslint-disable vue/block-lang -->
<script setup>
import '@onxmaps/yellowstone-ui/ys-chip-group'
import { FILTER_KEYS } from '@/services/filter-layer-service/constants'
import { FIELDS, SLUGS } from '@/fish/lib/taxonomy-constants'
import {
  EVENTS,
  FILTER_PROPERTIES,
  DISCOVER_ORIGINS,
} from '@/lib/enums-marketing'
import { computed } from 'vue'
import filterOption from '@/components/filter-option'
import {
  getCheckboxValues,
  setCheckbox,
  toggleCheckbox,
  addTotalFilter,
  setCheckboxDisplayActive,
  removeTotalFilter,
} from '@/services/filter-layer-service'
import marketingEventService from '@/services/marketing-event-service'
import { camera } from '@/services/map-service'

import { getMode } from '@/lib/modes'

const FILTER_NAME_MAP = {
  [FILTER_KEYS.DIFFICULTY]: 'Difficulty Level',
  [FILTER_KEYS.TECH_RATING_AS_DIFFICULTY]: 'Difficulty Level',
  [FILTER_KEYS.COLOR_RATING]: 'Difficulty Level',
  [FILTER_KEYS.PETS]: 'Dogs Allowed',
  [FILTER_KEYS.E_BIKES]: 'e-MTB Allowed',
  [FILTER_KEYS.ROUTE_TYPE]: 'Route Type',
  [FILTER_KEYS[FIELDS.FISH_TYPES]]: 'Fish Species',
  [FILTER_KEYS[SLUGS.LAUNCH]]: 'Boat Launch',
  [FILTER_KEYS[SLUGS.RAMP]]: 'Surface Type',
  [FILTER_KEYS[FIELDS.AMENITIES]]: 'Access & Amenities',
  [FILTER_KEYS[FIELDS.AREA]]: 'Lake Size',
}

const props = defineProps({
  filterKey: {
    type: String,
    required: false,
    default: undefined,
  },
  includeAll: {
    type: Boolean,
    required: false,
    default: true,
  },
  origin: {
    type: String,
    required: false,
    default: DISCOVER_ORIGINS.MODE,
  },
  disabled: {
    type: Boolean,
    required: false,
    default: false,
  },
})

const emit = defineEmits(['change'])

const displayValues = computed(() => {
  return Object.values(getCheckboxValues(props.includeAll, props.filterKey))
})

const checkboxValuesIncludingAll = computed(() =>
  Object.values(getCheckboxValues(true, props.filterKey))
)
const checkboxValuesExcludingAll = computed(() =>
  Object.values(getCheckboxValues(false, props.filterKey))
)

const mode = computed(() => getMode())

const chipGroupLabel = computed(
  () => FILTER_NAME_MAP[props.filterKey] ?? props.filterKey ?? 'Filter options'
)

const allButton = computed(() =>
  checkboxValuesIncludingAll.value.find(
    (val) => val.id === `${props.filterKey}_all`
  )
)

const toggleCheckboxHandler = (checkedItem) => {
  const { id, active, label } = checkedItem
  const wasAllButtonActive = allButton.value?.active

  if (id === `${props.filterKey}_all`) {
    // Clicking "All"
    handleAllCheckbox(checkedItem)
    sendMarketingEvent(!active, label)
  } else {
    // Clicked a filter option
    handleRegularCheckbox(checkedItem)

    // When "All" is active, all other events are active as well.
    sendMarketingEvent(wasAllButtonActive || !active, label)
  }

  emit('change')
}

const handleAllCheckbox = (checkedItem) => {
  // Is it already on?
  if (checkedItem.active) {
    // Turn all the selected filter group off
    checkboxValuesIncludingAll.value.forEach((item) => {
      setCheckbox(item, false, props.filterKey)
    })
    addTotalFilter(props.filterKey)
  } else {
    // Turn all the selected filter group on
    checkboxValuesExcludingAll.value?.forEach((item) => {
      setCheckboxDisplayActive(item, true, props.filterKey)
    })
    setCheckbox(checkedItem, true, props.filterKey)
    removeTotalFilter(props.filterKey)
  }
}

const handleRegularCheckbox = (checkedItem) => {
  // Is the "All" button also active?
  if (allButton.value?.active) {
    // Turn everything off, apply filters excluding current ID
    // Then toggle current ID on
    checkboxValuesIncludingAll.value.forEach((item) => {
      setCheckbox(item, false, props.filterKey)
    })
    toggleCheckbox(checkedItem, props.filterKey)
    addTotalFilter(props.filterKey)
  } else if (checkedItem.active) {
    toggleCheckbox(checkedItem, props.filterKey)
    // Only remove filter if the last item has been unchecked
    if (!appliedFilters.value?.length) {
      removeTotalFilter(props.filterKey)
    }
  } else {
    toggleCheckbox(checkedItem, props.filterKey)
    addTotalFilter(props.filterKey)
  }
}

const appliedFilters = computed(() => {
  const activeItems = checkboxValuesExcludingAll.value
    .filter((item) => item.active)
    .map((item) => item.label)
  return activeItems
})

const sendMarketingEvent = (isActive, filterChanged) => {
  const appliedValue = appliedFilters.value
  const activeItems = appliedValue.length ? appliedValue.join(', ') : 'none'

  const filterEventData = {
    name: EVENTS.FILTER_CHANGED,
    origin: props.origin,
    [FILTER_PROPERTIES.TYPE]:
      FILTER_NAME_MAP[props.filterKey] ?? props.filterKey,
    [FILTER_PROPERTIES.STATUS]: isActive ? 'on' : 'off',
    [FILTER_PROPERTIES.CHANGE]: filterChanged,
    [FILTER_PROPERTIES.APPLIED]: activeItems,
    min: null,
    max: null,
    map_mode: mode.value,
    zoom_level: camera()?.location?.height.toFixed(1) ?? null,
  }

  marketingEventService.publish(EVENTS.FILTER_CHANGED, {
    ...filterEventData,
  })
}
</script>
<style scoped>
.filter-options {
  padding: 0;
}
</style>
